import { useState, useCallback, useEffect } from 'react';

export interface ConsentPreferences {
  functional: boolean;    // Always true - required
  analytics: boolean;     // Omnisend, page tracking, etc.
  location: boolean;      // Approximate location (IP-based, not GPS)
}

const CONSENT_KEY = 'cookie_consent';
const CONSENT_UPDATED_EVENT = 'cookie-consent-updated';

const readStoredPreferences = (): ConsentPreferences | null => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export function useConsent() {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(readStoredPreferences);

  useEffect(() => {
    const syncPreferences = () => {
      setPreferences(readStoredPreferences());
    };

    window.addEventListener('storage', syncPreferences);
    window.addEventListener(CONSENT_UPDATED_EVENT, syncPreferences);

    return () => {
      window.removeEventListener('storage', syncPreferences);
      window.removeEventListener(CONSENT_UPDATED_EVENT, syncPreferences);
    };
  }, []);

  const hasConsented = preferences !== null;

  const savePreferences = useCallback((prefs: ConsentPreferences) => {
    setPreferences(prefs);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new Event(CONSENT_UPDATED_EVENT));
  }, []);

  const acceptAll = useCallback(() => {
    savePreferences({ functional: true, analytics: true, location: true });
  }, [savePreferences]);

  const acceptFunctionalOnly = useCallback(() => {
    savePreferences({ functional: true, analytics: false, location: false });
  }, [savePreferences]);

  const hasLocationConsent = preferences?.location ?? false;
  const hasAnalyticsConsent = preferences?.analytics ?? false;

  return {
    preferences,
    hasConsented,
    hasLocationConsent,
    hasAnalyticsConsent,
    acceptAll,
    acceptFunctionalOnly,
    savePreferences,
  };
}
